import clsx from 'clsx';
import { ArrowDownRight, ArrowUpRight, CalendarClock, Check, ChevronRight, CircleCheck, FileText, Hand, History, ListChecks, Minus, Receipt, Repeat, Rocket, TrendingUp, Wrench } from 'lucide-react';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import { useActions, useActivity, useCompleteOnboarding, useMeetings, useOnboarding, useProjects, useTasks } from '@/api';
import { ActionList } from '@/components/ActionList';
import { FeedbackDialog } from '@/components/FeedbackDialog';
import { describeAction } from '@/lib/actions';
import type { FixedProject, Metric, RetainerProject, Task } from '@/api/types';
import { PersonLink } from '@/components/PersonDialog';
import { Avatar, Sparkline, TaskGlyph } from '@/components/ui';
import { formatDate, formatDue, formatTime, formatWhen } from '@/lib/format';
import { sourceNote } from '@/lib/rhythm';
import { taskLabel } from '@/lib/status';
import { UploadButton } from '@/pages/Documents';
import type { ProjectContext } from './ProjectShell';

type Tone = 'waiting' | 'active' | 'neutral';

/**
 * One card per kind of thing on the page, in the same shape: an icon badge, a title with a
 * count, a line saying whose move it is. Only «Ждём от вас» with something in it is tinted:
 * that is the client's to-do, the rest is to read.
 */
function Block({
  tone = 'neutral',
  icon,
  title,
  count,
  hint,
  aside,
  className,
  tour,
  children,
}: {
  tone?: Tone;
  icon: React.ReactNode;
  title: string;
  count?: number;
  hint?: string;
  aside?: React.ReactNode;
  className?: string;
  /** Where the first-login guide points (`data-tour`). */
  tour?: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-tour={tour}
      className={clsx(
        'min-w-0 rounded-lg p-5',
        tone === 'waiting' ? 'bg-waiting/[0.05] shadow-[inset_0_0_0_1px_rgb(var(--waiting)/0.35)]' : 'bg-canvas shadow-hair',
        className,
      )}
    >
      <header className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span
            aria-hidden
            className={clsx(
              'grid size-8 shrink-0 place-items-center rounded-full [&>svg]:size-4',
              tone === 'waiting' ? 'bg-waiting/15 text-waiting' : tone === 'active' ? 'bg-active/10 text-active' : 'bg-sunken text-muted',
            )}
          >
            {icon}
          </span>
          <div className="min-w-0">
            <h2 className="flex items-baseline gap-1.5 text-sm font-semibold">
              {title}
              {!!count && <span className="num font-normal text-faint">{count}</span>}
            </h2>
            {hint && <p className="text-xs text-muted">{hint}</p>}
          </div>
        </div>
        {aside && <span className="shrink-0 pt-1 text-xs text-muted">{aside}</span>}
      </header>
      {children}
    </section>
  );
}

function TaskLine({ t }: { t: Task }) {
  const s = taskLabel(t);
  return (
    <li>
      <Link to={`tasks/${t.id}`} className="group -mx-2 flex items-start gap-2.5 rounded px-2 py-2 hover:bg-panel">
        <span className="mt-1">
          <TaskGlyph status={t.status} waiting={!!t.needsClient} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm leading-snug">{t.title}</span>
          <span className="block text-xs text-muted">
            {t.needsClient ? t.needsClient.note : s.label}
            {t.due && `, ${formatDue(t.due)}`}
          </span>
        </span>
        <ChevronRight className="mt-0.5 size-4 shrink-0 text-faint opacity-0 group-hover:opacity-100" />
      </Link>
    </li>
  );
}

function MilestoneBar({ p }: { p: FixedProject }) {
  const current = p.milestones.findIndex((m) => m.state === 'current');
  const m = p.milestones[current];
  return (
    <div className="w-full max-w-xs">
      <div className="flex gap-1" aria-hidden>
        {p.milestones.map((x) => (
          <span key={x.id} className={clsx('h-1.5 flex-1 rounded-full', x.state === 'done' ? 'bg-ink' : x.state === 'current' ? 'bg-ink/45' : 'bg-strong')} />
        ))}
      </div>
      <p className="mt-2 text-sm">
        <span className="font-medium">
          Этап {current + 1} из {p.milestones.length}: {m?.title}
        </span>
        {m?.due && <span className="text-muted">, до {formatDate(m.due)}</span>}
      </p>
      <p className="text-sm text-muted">Сдача проекта — {formatDate(p.finishBy)}</p>
    </div>
  );
}

function MetricTile({ m }: { m: Metric }) {
  // Without a known good direction the arrow stays neutral: up is not always a win.
  const good = m.trend === 'flat' || m.trend === m.better;
  const tone = !m.better || m.trend === 'flat' ? 'text-muted' : good ? 'text-done' : 'text-overdue';
  const Arrow = m.trend === 'up' ? ArrowUpRight : m.trend === 'down' ? ArrowDownRight : Minus;
  return (
    <div className="flex items-end justify-between gap-4 py-4">
      <div>
        <div className="text-sm text-muted">
          {m.label} {m.period}
        </div>
        <div className="num mt-1 text-xl font-semibold tracking-tight">{m.value}</div>
        {m.change && (
          <div className={clsx('mt-0.5 inline-flex items-center gap-1 text-xs', tone)}>
            {m.trend && <Arrow className="size-3.5" />}
            {m.change}
          </div>
        )}
      </div>
      <Sparkline values={m.series} className="text-muted" />
    </div>
  );
}

function Retainer({ p }: { p: RetainerProject }) {
  return (
    <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.4fr]">
      <Block icon={<TrendingUp />} title="Результаты" hint="Показатели из последнего отчёта" aside={p.metrics.length ? p.period : undefined}>
        {p.metrics.length ? (
          <>
            <div className="divide-y divide-line">
              {p.metrics.map((m) => (
                <MetricTile key={m.id} m={m} />
              ))}
            </div>
            <Link to="reports" className="mt-2 inline-block text-xs text-muted hover:text-ink">
              Отчёт целиком
            </Link>
          </>
        ) : (
          <p className="text-sm text-muted">Цифры появятся с первым отчётом: заявки, стоимость, охваты — то, что команда отмечает в отчёте за период.</p>
        )}
      </Block>
      <Block icon={<Repeat />} title="Регулярная работа" hint="Что делаем постоянно">
        {p.rhythm.length === 0 ? (
          <p className="text-sm text-muted">Регулярные работы пока не заведены.</p>
        ) : (
        <div className="overflow-x-auto"><table className="w-full min-w-[420px] text-left text-sm">
          <thead className="text-xs text-muted">
            <tr>
              <th className="py-2 pr-4 font-normal">Что</th>
              <th className="py-2 pr-4 font-normal">Как часто</th>
              <th className="py-2 pr-4 font-normal">Было</th>
              <th className="py-2 font-normal">Будет</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {p.rhythm.map((r) => (
              <tr key={r.id}>
                <td className="py-2.5 pr-4">
                  {r.source === 'linear' ? (
                    <Link to={r.taskKeys?.[0] ? `tasks/${r.taskKeys[0].toLowerCase()}` : 'tasks'} className="font-medium hover:underline">
                      {r.title}
                    </Link>
                  ) : (
                    <span className="font-medium">{r.title}</span>
                  )}
                  {sourceNote(r) && <span className="block text-xs text-faint">{sourceNote(r)}</span>}
                </td>
                <td className="py-2.5 pr-4 text-muted">{r.cadence}</td>
                <td className="py-2.5 pr-4 text-muted">{r.lastDone ? formatDate(r.lastDone) : '—'}</td>
                <td className={clsx('py-2.5', r.overdue && 'text-overdue')}>
                  {r.next ? formatDue(r.next) : '—'}
                  {r.overdue && <span className="block text-xs">просрочено</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
        )}
      </Block>
    </div>
  );
}

function Plan({ p }: { p: FixedProject }) {
  return (
    <Block className="mt-6" icon={<ListChecks />} title="План" hint="Этапы проекта и где мы сейчас">
      <ol className="grid gap-px overflow-hidden rounded-lg bg-line shadow-hair sm:[grid-template-columns:repeat(var(--n),minmax(0,1fr))]" style={{ ["--n" as string]: p.milestones.length }}>
        {p.milestones.map((m, i) => (
          <li key={m.id} className={clsx('bg-canvas p-4', m.state === 'current' && 'bg-panel')}>
            <div className="flex items-center gap-2 text-xs text-muted">
              <span className={clsx('num grid size-5 place-items-center rounded-full text-[10px] font-semibold', m.state === 'done' ? 'bg-ink text-inverse' : m.state === 'current' ? 'text-ink ring-2 ring-ink' : 'text-faint ring-1 ring-strong')}>
                {m.state === 'done' ? <Check className="size-3" strokeWidth={3} /> : i + 1}
              </span>
              {m.state === 'done' ? 'готово' : m.state === 'current' ? 'сейчас' : m.due ? `до ${formatDate(m.due)}` : ''}
            </div>
            <div className={clsx('mt-2 text-sm', m.state === 'next' ? 'text-muted' : 'font-medium')}>{m.title}</div>
          </li>
        ))}
      </ol>
    </Block>
  );
}

const activityIcon = { done: Check, status: ChevronRight, file: FileText, comment: ChevronRight, money: Receipt };

const onboardingCta = { access: 'Передать', materials: 'Загрузить', contact: 'Указать', meeting: 'Готово' } as const;

function Onboarding({ projectId }: { projectId: string }) {
  const { data: items } = useOnboarding(projectId);
  const complete = useCompleteOnboarding();
  if (!items?.length || items.every((i) => i.done)) return null;
  const done = items.filter((i) => i.done).length;
  return (
    <Block
      className="mt-6"
      tone="waiting"
      icon={<Rocket />}
      title="Запуск проекта"
      hint="Что нужно от вас, чтобы начать"
      aside={
        <span className="num">
          готово {done} из {items.length}
        </span>
      }
    >
      <div className="flex gap-1" aria-hidden>
        {items.map((i) => (
          <span key={i.id} className={clsx('h-1 flex-1 rounded-full', i.done ? 'bg-ink' : 'bg-strong')} />
        ))}
      </div>
      <ul className="mt-4 divide-y divide-line">
        {items.map((i) => (
          <li key={i.id} className="flex flex-wrap items-center gap-3 py-2.5">
            <span className={clsx('grid size-4 shrink-0 place-items-center rounded-sm', i.done ? 'bg-ink text-inverse' : 'ring-1 ring-strong')}>{i.done && <Check className="size-3" strokeWidth={3} />}</span>
            <span className="min-w-0 flex-1">
              <span className={clsx('block text-sm', i.done && 'text-muted line-through decoration-faint')}>{i.title}</span>
              {!i.done && <span className="block text-xs text-muted">{i.hint}</span>}
            </span>
            {!i.done &&
              (i.kind === 'access' ? (
                <Link className="btn-secondary h-7 px-2.5 text-xs" to={`/access?project=${projectId}&title=${encodeURIComponent(i.title)}&onboarding=${i.id}`}>
                  {onboardingCta[i.kind]}
                </Link>
              ) : i.kind === 'materials' ? (
                // Not done until a file has actually gone up.
                <UploadButton compact projectId={projectId} label={onboardingCta[i.kind]} onUploaded={() => complete.mutate(i.id)} />
              ) : (
                <button className="btn-secondary h-7 px-2.5 text-xs" onClick={() => complete.mutate(i.id)}>
                  {onboardingCta[i.kind]}
                </button>
              ))}
          </li>
        ))}
      </ul>
    </Block>
  );
}

export function ProjectNow() {
  const { project: p } = useOutletContext<ProjectContext>();
  const { data: tasks } = useTasks(p.id);
  const { data: activity } = useActivity(p.id);
  const { data: actions } = useActions();
  const { data: projects } = useProjects();
  const { data: meetings } = useMeetings(p.id);
  const [params, setParams] = useSearchParams();
  const single = projects?.length === 1;
  // Everything this project waits on the client for; with one project, money and access too.
  const mine = (actions ?? []).filter((a) => {
    // The launch block shows the launch items; access asked outside the launch waits here.
    if (a.kind === 'onboarding' && a.item.scope !== 'access') return false;
    const v = describeAction(a);
    return v.projectId === p.id || (single && !v.projectId);
  });
  const doing = tasks?.filter((t) => (t.status === 'in_progress' || t.status === 'review') && !t.needsClient) ?? [];
  const next = meetings?.find((m) => !m.outcome && m.status !== 'canceled' && new Date(m.at).getTime() > Date.now());

  return (
    <>
      <section className="frame flex flex-wrap items-end justify-between gap-8 px-6 py-6">
        <div className="max-w-2xl">
          <p className="text-xs text-muted">Положение дел на {formatDate(new Date().toISOString())}</p>
          <p className="mt-1.5 text-lg font-semibold leading-snug tracking-tight">{p.now}</p>
          <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted">
            <Avatar person={p.owner} size="sm" />
            <span>
              Ведёт <PersonLink person={p.owner} className="rounded font-medium text-ink hover:underline hover:underline-offset-2" />, обновлено {formatWhen(p.updatedAt)}
            </span>
            {next && (
              <Link to="meetings" className="ml-2 inline-flex items-center gap-1.5 text-ink hover:underline">
                <CalendarClock className="size-4" /> {next.title}: {formatDate(next.at)}, {formatTime(next.at)}
              </Link>
            )}
          </p>
        </div>
        {p.kind === 'fixed' ? (
          <MilestoneBar p={p} />
        ) : (
          <div className="text-sm">
            <div className="font-medium">Регулярные платежи, работаем постоянно</div>
            <div className="text-muted">Смотрим на результат недели, а не на этапы</div>
          </div>
        )}
      </section>

      <Onboarding projectId={p.id} />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.3fr_1fr]">
        <Block
          tone={mine.length ? 'waiting' : 'neutral'}
          icon={mine.length ? <Hand /> : <CircleCheck />}
          title="Ждём от вас"
          tour="waiting"
          count={mine.length}
          hint={mine.length ? 'Без этого работа дальше не пойдёт' : 'Сейчас ничего не нужно — команда работает'}
        >
          {mine.length > 0 && <ActionList actions={mine} columns={1} />}
        </Block>
        <Block tone="active" icon={<Wrench />} title="В работе у команды" count={doing.length} hint="Что команда делает прямо сейчас" aside={<Link to="tasks" className="hover:text-ink">все задачи</Link>}>
          {doing.length ? (
            <ul>
              {doing.slice(0, 6).map((t) => (
                <TaskLine key={t.id} t={t} />
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">Задач в работе сейчас нет.</p>
          )}
          {doing.length > 6 && <p className="pt-1 text-xs text-muted">и ещё {doing.length - 6}</p>}
        </Block>
      </div>

      {p.kind === 'retainer' && <Retainer p={p} />}
      {p.kind === 'fixed' && <Plan p={p} />}

      <Block className="mt-6" icon={<History />} title="Что изменилось" hint="Последние события по проекту">
        {activity && activity.length === 0 && <p className="text-sm text-muted">Пока событий нет.</p>}
        <ul className="grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2">
          {activity?.map((a) => {
            const Icon = activityIcon[a.kind];
            const body = (
              <>
                <Icon className="mt-0.5 size-3.5 shrink-0 text-muted" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm leading-snug">{a.text}</span>
                  <span className="block text-xs text-muted">
                    {a.who.side === 'client' ? 'вы' : a.who.name}, {formatWhen(a.at)}
                  </span>
                </span>
              </>
            );
            return (
              <li key={a.id}>
                {a.taskId ? (
                  <Link to={`tasks/${a.taskId}`} className="flex gap-2.5 hover:underline">
                    {body}
                  </Link>
                ) : (
                  <div className="flex gap-2.5">{body}</div>
                )}
              </li>
            );
          })}
        </ul>
      </Block>

      {params.get('feedback') && (
        <FeedbackDialog
          open
          projectId={p.id}
          context={p.kind === 'fixed' ? `Этап «${p.milestones.filter((m) => m.state === 'done').at(-1)?.title ?? 'первый'}» завершён` : 'Месяц работы'}
          onClose={() => setParams({})}
        />
      )}
    </>
  );
}
